import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconArrowRight } from '@iwoca/icons';
import { Input, Label, Message } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { FormProvider, useForm } from 'react-hook-form';

import styles from './CompanyDetailsDE.module.css';
import { schema } from './CompanyDetailsDE.schema';
import { CompanyInputDE } from './CompanyInputDE/CompanyInputDE';
import { compareFoundationDate } from './foundationDateComparison';
import TurnoverInputDE from './TurnoverInputDE';
import {
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../api/lendingApi/lendingApiQueries';
import { ISOLATED_SPLIT_TESTS } from '../../../constants';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useIsInDECompanySearchUITest } from '../../../hooks/useIsInDECompanySearchUITest';
import { useSignupFieldTracking } from '../../../hooks/useSignupFieldTracking';
import { useSignupRouterDE } from '../../../hooks/useSignupRouter/useSignupRouterDE';
import {
  TGetStateResponseCompanyDE,
  TGetStateResponseDE,
} from '../../../lapiOverrideTypesDE';
import {
  getAmplitudeDefaultEventProperties,
  getAmplitudeDeviceCategory,
} from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { EditAmount } from '../../EditAmount/EditAmount';
import {
  denormaliseCompanyDataDE,
  normaliseStepDataDE,
} from '../../helpers/stateNormalisation';
import { BUSINESS_STEP_ID } from '../../helpers/steps';
import RouteSpinner from '../../RouteSpinner/RouteSpinner';
import AccountCreatedModal from '../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';

export type TCompanyDetailsDataDE = {
  isManualInput?: boolean;
  companyName: string;
  crefoId?: string;
  crefoFoundationDate?: string;
  buildingNumber: string;
  street: string;
  postcode: string;
  town: string;
  legalForm?: TGetStateResponseCompanyDE['type'];
  startDateMonth: string;
  startDateYear: string;
  last12MonthsTurnover: string;
  website?: string;
};

type TProps = {
  handleStateUpdateError: (
    error: Error,
    isIncludedInCompanySearchUITest: boolean,
  ) => void;
};

const CompanyDetailsDE = ({ handleStateUpdateError }: TProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchedCompaniesCount, setSearchedCompaniesCount] = useState(0);

  const { state, isStateFetching } = useGetStateQuery<TGetStateResponseDE>();

  const { normalisedState } = useGetSelectStateQuery(
    (data: TGetStateResponseDE) => normaliseStepDataDE(data)[BUSINESS_STEP_ID],
  );

  const stateKey = useContextStateKey();
  const { navigateSignupDE } = useSignupRouterDE();

  const { t } = useLingui();

  const {
    isIncludedInCompanySearchUITest,
    isLoading: isUITestLoading,
    splitTestBranch,
  } = useIsInDECompanySearchUITest();

  useEffect(() => {
    if (!isUITestLoading && splitTestBranch) {
      tracking.ampli.addedToSplitTest({
        Branch: splitTestBranch,
        'Test name': ISOLATED_SPLIT_TESTS.DE_COMPANY_SEARCH_UI_TEST,
      });
    }
  }, [isUITestLoading, splitTestBranch]);

  const methods = useForm<TCompanyDetailsDataDE>({
    resolver: yupResolver(schema),
    defaultValues: normalisedState,
  });

  const { fieldProps, reportCompletion } =
    useSignupFieldTracking('Business details');

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Business details',
      client_device: getAmplitudeDeviceCategory(),
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onSubmit = async (values: TCompanyDetailsDataDE) => {
    if (!state || !stateKey) {
      return;
    }

    const denormalisedState = denormaliseCompanyDataDE(state, values);

    const foundationDateComparison = compareFoundationDate({
      crefoFoundationDate: values.crefoFoundationDate,
      startDateMonth: values.startDateMonth,
      startDateYear: values.startDateYear,
    });

    tracking.ampli.businessDetailsSubmitted({
      ...getAmplitudeDefaultEventProperties(),
      searched_companies_count: searchedCompaniesCount,
      manually_entered_company: Boolean(values.isManualInput),
      contains_crefoid: Boolean(values.crefoId),
      ...(foundationDateComparison && {
        foundation_date_exact_match: foundationDateComparison.exactMatch,
        foundation_date_month_delta: foundationDateComparison.monthDelta,
      }),
    });

    try {
      setIsSubmitting(true);
      await navigateSignupDE('schritt-2', stateKey, denormalisedState.data);
    } catch (error: unknown) {
      if (error instanceof Error) {
        handleStateUpdateError(error, isIncludedInCompanySearchUITest);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isUITestLoading) return <RouteSpinner />;

  return (
    <>
      <div className={styles.container}>
        <EditAmount currentStep="company_details" />
        <h1 className={styles.heading}>
          {isIncludedInCompanySearchUITest ? (
            <Trans>Your company</Trans>
          ) : (
            <Trans>Business details</Trans>
          )}
        </h1>
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={styles.form}
            {...fieldProps}
          >
            <div className={styles.formFieldContainer}>
              <CompanyInputDE
                searchedCompaniesCount={searchedCompaniesCount}
                setSearchedCompaniesCount={setSearchedCompaniesCount}
                reportCompanyNameCompletion={(isComplete) =>
                  reportCompletion('companyName', isComplete)
                }
              />
              <div className={styles.formField}>
                <Label className={styles.formLabel}>
                  {isIncludedInCompanySearchUITest ? (
                    <Trans>Founding date</Trans>
                  ) : (
                    <Trans>When was your company founded?</Trans>
                  )}
                </Label>
                <div className={styles.tradingFromContainer}>
                  <Input
                    {...register('startDateMonth')}
                    placeholder={t`MM`}
                    data-testid="start-date-month"
                    error={errors.startDateMonth?.message}
                  />
                  <Input
                    {...register('startDateYear')}
                    placeholder={t`YYYY`}
                    data-testid="start-date-year"
                  />
                </div>
                {errors.startDateYear && (
                  <Message
                    className={styles.foundingDateError}
                    message={errors.startDateYear.message || ''}
                    type="error"
                  />
                )}
              </div>
              <Input
                {...register('website')}
                wrapperClassName={styles.formField}
                label={t`Website (optional)`}
                placeholder={t`www.example.com`}
                error={errors.website?.message}
              />
              <div className={styles.formField}>
                <TurnoverInputDE />
              </div>
            </div>
            <Button
              className={styles.submit}
              loading={isSubmitting || isStateFetching}
              type="submit"
            >
              <Trans>Next step</Trans>
              <IconArrowRight />
            </Button>
          </form>
        </FormProvider>
      </div>
      {stateKey && <AccountCreatedModal stateKey={stateKey} />}
    </>
  );
};

export default CompanyDetailsDE;
